import { API_ROOT, joinUrl } from '../config/api';

/**
 * Match backend `encodeUploadPathSegments` so fallback URLs work for spaces/special chars.
 * @param {string} relativePath
 */
export function encodeUploadPathSegments(relativePath) {
  return String(relativePath)
    .replace(/^\/+/, '')
    .replace(/\\/g, '/')
    .split('/')
    .filter(Boolean)
    .map((seg) => encodeURIComponent(seg))
    .join('/');
}

/** Dev-only: CRA often runs on :3000 while API is on :5001. */
export function normalizeDevServerPorts(url) {
  return String(url || '')
    .replace(':3000', ':5001')
    .replace(':3001', ':5001');
}

function effectiveApiRoot() {
  const root = String(API_ROOT || '').trim().replace(/\/+$/, '');
  if (root) return root;
  const path = (process.env.REACT_APP_API_BASE_PATH || '/api').trim().replace(/\/+$/, '') || '/api';
  return joinUrl('http://localhost:5001', path);
}

/**
 * Public file URL for a relative uploads path (e.g. package-images/foo.jpg).
 * Uses `${API_ROOT}/uploads/` — not the admin site origin.
 *
 * @param {string|null|undefined} imagePath
 * @param {{ defaultSubdirectory?: string }} [options]
 * @returns {string|null}
 */
export function publicUploadFileUrlFromRelativePath(imagePath, options = {}) {
  const { defaultSubdirectory } = options;
  if (imagePath == null) return null;
  const s = String(imagePath).trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) {
    return normalizeDevServerPorts(s);
  }

  let path = s.startsWith('/') ? s.slice(1) : s;
  path = path.replace(/\\/g, '/');
  if (path.startsWith('uploads/')) {
    path = path.slice('uploads/'.length);
  }

  if (defaultSubdirectory) {
    const d = String(defaultSubdirectory).replace(/\/+$/, '').replace(/^\/+/, '');
    if (d && !path.startsWith(`${d}/`)) {
      path = `${d}/${path}`;
    }
  }

  const encoded = encodeUploadPathSegments(path);
  const base = effectiveApiRoot();
  return `${base}/uploads/${encoded}`;
}

/**
 * Prefer absolute `imageUrl` from the API; otherwise build from `imagePath`.
 *
 * @param {string|null|undefined} imagePath
 * @param {string|null|undefined} imageUrl
 * @param {{ defaultSubdirectory?: string }} [options]
 * @returns {string|null}
 */
export function resolveUploadImageSrc(imagePath, imageUrl, options = {}) {
  const trimmed = imageUrl != null ? String(imageUrl).trim() : '';
  if (trimmed) {
    if (/^https?:\/\//i.test(trimmed)) {
      return normalizeDevServerPorts(trimmed);
    }
    if (trimmed.startsWith('/')) {
      try {
        const base = effectiveApiRoot();
        const absBase = base.match(/^https?:\/\//) ? base : `http://${base}`;
        const origin = new URL(absBase).origin;
        return normalizeDevServerPorts(`${origin}${trimmed}`);
      } catch {
        return normalizeDevServerPorts(trimmed);
      }
    }
    return publicUploadFileUrlFromRelativePath(trimmed, options);
  }
  return publicUploadFileUrlFromRelativePath(imagePath, options);
}
